import { useMemo } from 'react';
import styled from 'styled-components';
import type { WithSX } from '@elfsight/lib-utils';
import { applySX } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import type { FormField } from '../contexts';
import { FieldComponent, type FieldComponentProps } from './FieldComponent';

const FULL_WIDTH = 100;
const GRID_ROWS_COUNT = 3; // label, input, error message

interface FieldsGridProps
  extends Omit<
    FieldComponentProps,
    'field' | 'columns' | 'invalidErrorMessage'
  > {
  fields: FormField[];
  getInvalidErrorMessage?: (field: FormField) => string;
}

interface FieldWithColumns extends FormField {
  columns: [number, number];
}

interface FieldsRow {
  id: string;
  fields: FieldWithColumns[];
}

function getRowId(fields: FieldWithColumns[]) {
  return fields.map(({ id }) => id).join('_');
}

function isFieldDisplayed({ type }: FieldWithColumns) {
  return type !== 'hidden';
}

export function FieldsGrid({
  fields,
  getInvalidErrorMessage,
  ...forwardingProps
}: FieldsGridProps) {
  const rows = useFieldsRows(fields);

  return (
    <Grid>
      {rows.map(({ id, fields: rowFields }) => {
        const firstFieldId = rowFields.find(isFieldDisplayed)?.id;
        const lastFieldId = rowFields.findLast(isFieldDisplayed)?.id;

        return (
          <GridRow key={id}>
            {rowFields.map((field) => {
              return (
                <StyledFieldComponent
                  key={field.id}
                  {...forwardingProps}
                  field={field}
                  columns={field.columns}
                  invalidErrorMessage={getInvalidErrorMessage?.(field)}
                  contentDirection="row"
                  $sx={{
                    paddingLeft: field.id === firstFieldId ? 0 : undefined,
                    paddingRight: field.id === lastFieldId ? 0 : undefined
                  }}
                  addBottomMarginToLastChild
                />
              );
            })}
          </GridRow>
        );
      })}
    </Grid>
  );
}

const Grid = styled.div.attrs({
  className: createReadableClass({
    component: 'fields-grid'
  })
})`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const GridRow = styled.div.attrs({
  className: createReadableClass({
    component: 'fields-grid',
    element: 'row'
  })
})`
  display: grid;
  grid-template-columns: repeat(${FULL_WIDTH}, 1fr);
  grid-template-rows: repeat(${GRID_ROWS_COUNT}, max-content);
`;

const StyledFieldComponent = styled(FieldComponent).attrs<WithSX>({
  className: createReadableClass({
    component: 'fields-grid',
    element: 'field'
  })
})`
  ${applySX};

  &:first-child {
    padding-left: 0;
  }
`;

function getFieldWidth(field: FormField) {
  if (field.type === 'hidden') {
    return 0;
  }

  return field.width ?? FULL_WIDTH;
}

function useFieldsRows(fields: FormField[]) {
  return useMemo(() => {
    const fieldsRows: FieldsRow[] = [];
    let currentRow: FieldWithColumns[] = [];
    let currentRowWidth = 0;

    fields.forEach((field) => {
      const fieldWidth = getFieldWidth(field);
      let newRowWidth = currentRowWidth + fieldWidth;

      if (newRowWidth > FULL_WIDTH && currentRow.length > 0) {
        fieldsRows.push({ id: getRowId(currentRow), fields: currentRow });

        currentRow = [];
        currentRowWidth = 0;
        newRowWidth = fieldWidth;
      }

      currentRow.push({
        ...field,
        columns: [currentRowWidth + 1, newRowWidth + 1] as [number, number]
      });
      currentRowWidth = newRowWidth;
    });

    if (currentRow.length) {
      fieldsRows.push({ id: getRowId(currentRow), fields: currentRow });
    }

    return fieldsRows;
  }, [fields]);
}
